import type { User, Session, SessionGrants, LoginCredentials, Role } from '@shared/types/User';
import {
  roleForTenant,
  roleForTenantFromGrants,
  sessionGrantsFromUser,
} from '@shared/access/tenantUserRole';
import { PasswordHasher } from './PasswordHasher';
import { uuid } from '@shared/utils/uuid';
import {
  generateRecoveryCode,
  hashRecoveryCode,
  verifyRecoveryCode,
} from '@application/auth/LocalPasswordRecovery';

/**
 * Service d'authentification.
 * Session token = random 32 bytes base64, durée 8h.
 * Stocké en sessionStorage (effacé à la fermeture du navigateur).
 */
export interface IUserRepository {
  findByEmail(email: string, tenantId?: string): Promise<User | undefined>;
  findById(id: string): Promise<User | null>;
  create(user: Omit<User, 'id'>): Promise<User>;
  update(user: User): Promise<void>;
}

/** Messages lisibles des refus d'écriture ou de session : un formulaire qui affiche `err.message` ne montre jamais un code technique. */
const AUTH_ERROR_MESSAGES: Partial<Record<string, string>> = {
  LICENCE_EXPIRED:
    'Licence en lecture seule : vos données restent consultables et exportables, mais la saisie est bloquée. Contactez Nexxus.',
  INSUFFICIENT_ROLE: 'Votre rôle ne permet pas cette action.',
  SESSION_EXPIRED: 'Session expirée — veuillez vous reconnecter.',
  OFFLINE_IDENTITY_EXPIRED:
    'Connexion à Internet requise : votre compte n’a pas été vérifié en ligne depuis plus de 30 jours.',
  FIRST_LOGIN_ONLINE_REQUIRED:
    'Connexion à Internet requise pour ouvrir une première session sur ce poste avec un compte du serveur.',
};

export class AuthError extends Error {
  constructor(
    public readonly code:
      | 'INVALID_CREDENTIALS'
      | 'USER_INACTIVE'
      | 'SESSION_EXPIRED'
      | 'INSUFFICIENT_ROLE'
      | 'LICENCE_EXPIRED'
      | 'TOO_MANY_ATTEMPTS'
      | 'RECOVERY_INVALID'
      | 'SUPPORT_UNLOCK_FAILED'
      | 'OFFLINE_IDENTITY_EXPIRED'
      | 'FIRST_LOGIN_ONLINE_REQUIRED'
      | 'NETWORK_REQUIRED',
  ) {
    super(AUTH_ERROR_MESSAGES[code] ?? code);
    this.name = 'AuthError';
  }
}

export class AuthService {
  static readonly SESSION_DURATION_MS = 8 * 60 * 60 * 1000; // A-34 : 8 h
  /** A-34 — limite de tentatives de connexion avant verrouillage temporaire. */
  static readonly MAX_LOGIN_ATTEMPTS = 5;
  static readonly LOCKOUT_MS = 15 * 60 * 1000;

  private failedAttempts = new Map<string, { count: number; lockedUntil?: number }>();
  static readonly STORAGE_KEY = 'nexxus_session';
  static readonly SESSION_EXPIRED_NOTICE_KEY = 'nexxus_session_expired_notice';

  static consumeSessionExpiredNotice(): boolean {
    try {
      const flagged = sessionStorage.getItem(AuthService.SESSION_EXPIRED_NOTICE_KEY) === '1';
      sessionStorage.removeItem(AuthService.SESSION_EXPIRED_NOTICE_KEY);
      return flagged;
    } catch {
      return false;
    }
  }

  static markSessionExpiredNotice(): void {
    try {
      sessionStorage.setItem(AuthService.SESSION_EXPIRED_NOTICE_KEY, '1');
    } catch {
      /* quota / mode privé */
    }
  }

  private remoteRefresh: (() => Promise<Session | null>) | null = null;

  constructor(private userRepo: IUserRepository) {}

  /** Bascule le stockage utilisateurs (ex. Dexie → SQLite chiffré). */
  useUserRepository(repo: IUserRepository): void {
    this.userRepo = repo;
  }

  /** Mode distant : revalidation session via business-api /v1/auth/me */
  setRemoteSessionRefresh(refresh: (() => Promise<Session | null>) | null): void {
    this.remoteRefresh = refresh;
  }

  /**
   * Inscrit un nouvel utilisateur.
   * Retourne aussi un code de récupération à noter (mode local).
   */
  async register(
    tenant_id: string,
    email: string,
    password: string,
    nom: string,
    prenom: string,
    role: Role,
  ): Promise<{ user: User; recoveryCode: string }> {
    if (password.length < 8) throw new Error('Mot de passe trop court (min 8 caractères)');
    const normalized = email.toLowerCase().trim();
    const existing = await this.userRepo.findByEmail(normalized, tenant_id);
    if (existing) {
      throw new Error('EMAIL_ALREADY_REGISTERED');
    }
    const salt = PasswordHasher.generateSalt();
    const hash = await PasswordHasher.hash(password, salt);
    const recoveryCode = generateRecoveryCode();
    const recovery = await hashRecoveryCode(recoveryCode);
    const user = await this.userRepo.create({
      tenant_id,
      email: normalized,
      nom,
      prenom,
      role,
      password_hash: hash,
      password_salt: salt,
      recovery_code_hash: recovery.hash,
      recovery_code_salt: recovery.salt,
      actif: true,
      cree_le: new Date().toISOString(),
    });
    return { user, recoveryCode };
  }

  async findUserByEmail(email: string, tenantId?: string): Promise<User | undefined> {
    return this.userRepo.findByEmail(email.toLowerCase().trim(), tenantId);
  }

  async resetPassword(userId: string, password: string): Promise<void> {
    const session = await this.requireRoleFresh('admin', 'super_admin');
    const user = await this.userRepo.findById(userId);
    if (!user || user.tenant_id !== session.tenant_id) throw new Error('Utilisateur introuvable');
    if (password.length < 8) throw new Error('Mot de passe trop court (min 8 caractères)');
    const salt = PasswordHasher.generateSalt();
    await this.userRepo.update({
      ...user,
      password_salt: salt,
      password_hash: await PasswordHasher.hash(password, salt),
    });
  }

  /**
   * Réinitialise le mot de passe en mode local via le code de récupération.
   * Le code est invalidé après succès (nouveau code renvoyé).
   */
  async resetPasswordWithRecoveryCode(
    email: string,
    recoveryCode: string,
    newPassword: string,
    tenantId?: string,
  ): Promise<{ recoveryCode: string }> {
    if (newPassword.length < 8) throw new Error('Mot de passe trop court (min 8 caractères)');
    const normalized = email.toLowerCase().trim();
    const gate = this.failedAttempts.get(`recovery:${normalized}`);
    if (gate?.lockedUntil && gate.lockedUntil > Date.now()) {
      throw new AuthError('TOO_MANY_ATTEMPTS');
    }

    const user = await this.userRepo.findByEmail(normalized, tenantId);
    if (!user || !user.actif) {
      this.recordFailedRecovery(normalized);
      throw new AuthError('RECOVERY_INVALID');
    }
    if (!user.recovery_code_hash || !user.recovery_code_salt) {
      throw new AuthError('RECOVERY_INVALID');
    }
    const ok = await verifyRecoveryCode(
      recoveryCode,
      user.recovery_code_hash,
      user.recovery_code_salt,
    );
    if (!ok) {
      this.recordFailedRecovery(normalized);
      throw new AuthError('RECOVERY_INVALID');
    }

    this.failedAttempts.delete(`recovery:${normalized}`);
    const salt = PasswordHasher.generateSalt();
    const nextCode = generateRecoveryCode();
    const recovery = await hashRecoveryCode(nextCode);
    await this.userRepo.update({
      ...user,
      password_salt: salt,
      password_hash: await PasswordHasher.hash(newPassword, salt),
      recovery_code_hash: recovery.hash,
      recovery_code_salt: recovery.salt,
    });
    return { recoveryCode: nextCode };
  }

  /**
   * Déblocage via code support Nexxus (généré dans admin.nexxus.ga).
   * Nécessite Internet une fois pour vérifier le code auprès de licensing-api.
   */
  async resetPasswordWithSupportUnlock(
    email: string,
    supportCode: string,
    newPassword: string,
    tenantId: string | undefined,
    verifyRemote: (input: {
      code: string;
      tenantId?: string;
    }) => Promise<{ ok: boolean; tenant_id?: string }>,
  ): Promise<{ recoveryCode: string }> {
    if (newPassword.length < 8) throw new Error('Mot de passe trop court (min 8 caractères)');
    const normalized = email.toLowerCase().trim();
    const gate = this.failedAttempts.get(`recovery:${normalized}`);
    if (gate?.lockedUntil && gate.lockedUntil > Date.now()) {
      throw new AuthError('TOO_MANY_ATTEMPTS');
    }

    let verified: { ok: boolean; tenant_id?: string };
    try {
      verified = await verifyRemote({ code: supportCode, tenantId });
    } catch {
      throw new AuthError('NETWORK_REQUIRED');
    }
    if (!verified.ok) {
      this.recordFailedRecovery(normalized);
      throw new AuthError('SUPPORT_UNLOCK_FAILED');
    }
    if (tenantId && verified.tenant_id && verified.tenant_id !== tenantId) {
      this.recordFailedRecovery(normalized);
      throw new AuthError('SUPPORT_UNLOCK_FAILED');
    }

    const user = await this.userRepo.findByEmail(normalized, tenantId);
    if (!user || !user.actif) {
      this.recordFailedRecovery(normalized);
      throw new AuthError('SUPPORT_UNLOCK_FAILED');
    }
    if (user.role !== 'admin' && user.role !== 'super_admin') {
      this.recordFailedRecovery(normalized);
      throw new AuthError('SUPPORT_UNLOCK_FAILED');
    }

    this.failedAttempts.delete(`recovery:${normalized}`);
    const salt = PasswordHasher.generateSalt();
    const nextCode = generateRecoveryCode();
    const recovery = await hashRecoveryCode(nextCode);
    await this.userRepo.update({
      ...user,
      password_salt: salt,
      password_hash: await PasswordHasher.hash(newPassword, salt),
      recovery_code_hash: recovery.hash,
      recovery_code_salt: recovery.salt,
    });
    return { recoveryCode: nextCode };
  }

  /** Attribue un code de récupération (admin ou premier login sans code). */
  async assignRecoveryCode(userId: string): Promise<string> {
    const user = await this.userRepo.findById(userId);
    if (!user) throw new Error('Utilisateur introuvable');
    const recoveryCode = generateRecoveryCode();
    const recovery = await hashRecoveryCode(recoveryCode);
    await this.userRepo.update({
      ...user,
      recovery_code_hash: recovery.hash,
      recovery_code_salt: recovery.salt,
    });
    return recoveryCode;
  }

  private recordFailedRecovery(email: string): void {
    const key = `recovery:${email}`;
    const prev = this.failedAttempts.get(key) ?? { count: 0 };
    const count = prev.count + 1;
    if (count >= AuthService.MAX_LOGIN_ATTEMPTS) {
      this.failedAttempts.set(key, {
        count,
        lockedUntil: Date.now() + AuthService.LOCKOUT_MS,
      });
    } else {
      this.failedAttempts.set(key, { count });
    }
  }

  /** Authentifie et crée une session (A-34 : 8 h, sessionStorage, limite tentatives). */
  async login(creds: LoginCredentials): Promise<Session> {
    const email = creds.email.toLowerCase().trim();
    const gate = this.failedAttempts.get(email);
    if (gate?.lockedUntil && gate.lockedUntil > Date.now()) {
      throw new AuthError('TOO_MANY_ATTEMPTS');
    }

    const user = await this.userRepo.findByEmail(email, creds.tenant_id);
    if (!user) {
      this.recordFailedLogin(email);
      throw new AuthError('INVALID_CREDENTIALS');
    }
    if (!user.actif) throw new AuthError('USER_INACTIVE');
    const ok = await PasswordHasher.verify(creds.password, user.password_hash, user.password_salt);
    if (!ok) {
      this.recordFailedLogin(email);
      throw new AuthError('INVALID_CREDENTIALS');
    }

    this.failedAttempts.delete(email);
    if (!user.recovery_code_hash || !user.recovery_code_salt) {
      const recoveryCode = generateRecoveryCode();
      const recovery = await hashRecoveryCode(recoveryCode);
      await this.userRepo.update({
        ...user,
        recovery_code_hash: recovery.hash,
        recovery_code_salt: recovery.salt,
        derniere_connexion: new Date().toISOString(),
      });
      // Conservé une seule fois pour affichage post-login (mode local)
      try {
        sessionStorage.setItem('nexxus_recovery_code_once', recoveryCode);
      } catch {
        /* ignore */
      }
    } else {
      await this.userRepo.update({ ...user, derniere_connexion: new Date().toISOString() });
    }
    const session: Session = {
      user_id: user.id,
      tenant_id: user.tenant_id,
      role: user.role,
      expire_le: new Date(Date.now() + AuthService.SESSION_DURATION_MS).toISOString(),
      token: uuid('tok'),
      grants: sessionGrantsFromUser(user),
    };
    this.persistSession(session);
    return session;
  }

  /** Persiste la session (sessionStorage + localStorage de secours pour Electron). */
  private persistSession(session: Session): void {
    const raw = JSON.stringify(session);
    sessionStorage.setItem(AuthService.STORAGE_KEY, raw);
    try {
      localStorage.setItem(AuthService.STORAGE_KEY, raw);
    } catch {
      /* quota / mode privé */
    }
  }

  /**
   * Réécrit la session en stockage (reprise après désync shell / remote login).
   * Ne valide pas le mot de passe — réservé aux flux internes déjà authentifiés.
   */
  restoreSession(session: Session): void {
    if (!session.user_id || !session.role || !session.tenant_id) return;
    const expire =
      new Date(session.expire_le).getTime() > Date.now()
        ? session.expire_le
        : new Date(Date.now() + AuthService.SESSION_DURATION_MS).toISOString();
    this.persistSession({ ...session, expire_le: expire });
  }

  /**
   * Prolonge la session (fenêtre glissante) lors d’une activité utilisateur.
   * Évite une déconnexion silencieuse pendant une saisie longue.
   */
  touchSession(): Session | null {
    const s = this.readRawSession();
    if (!s) return null;
    if (new Date(s.expire_le) <= new Date()) {
      this.logout();
      return null;
    }
    const updated: Session = {
      ...s,
      expire_le: new Date(Date.now() + AuthService.SESSION_DURATION_MS).toISOString(),
    };
    this.persistSession(updated);
    return updated;
  }

  private readRawSession(): Session | null {
    const parse = (raw: string | null): Session | null => {
      if (!raw) return null;
      try {
        const session = JSON.parse(raw) as Session;
        return session?.user_id && session?.role ? session : null;
      } catch {
        return null;
      }
    };

    const fromSession = parse(sessionStorage.getItem(AuthService.STORAGE_KEY));
    const fromLocal = parse(localStorage.getItem(AuthService.STORAGE_KEY));
    if (!fromSession && !fromLocal) return null;

    const now = Date.now();
    const isValid = (s: Session): boolean => new Date(s.expire_le).getTime() > now;

    if (fromSession && fromLocal) {
      const sameIdentity =
        fromSession.token === fromLocal.token || fromSession.user_id === fromLocal.user_id;
      if (sameIdentity) {
        // Expiration d’un seul côté = session expirée (ne pas « revivre » via l’autre stockage)
        if (!isValid(fromSession) || !isValid(fromLocal)) {
          return !isValid(fromSession) ? fromSession : fromLocal;
        }
        return fromSession;
      }
      // Sessions différentes (ex. login distant vs reste stale) : préférer la valide
      if (isValid(fromLocal)) return fromLocal;
      if (isValid(fromSession)) return fromSession;
      return fromSession;
    }

    return fromSession ?? fromLocal;
  }

  private recordFailedLogin(email: string): void {
    const prev = this.failedAttempts.get(email) ?? { count: 0 };
    const count = prev.count + 1;
    if (count >= AuthService.MAX_LOGIN_ATTEMPTS) {
      this.failedAttempts.set(email, {
        count,
        lockedUntil: Date.now() + AuthService.LOCKOUT_MS,
      });
    } else {
      this.failedAttempts.set(email, { count });
    }
  }

  /** Récupère la session active si valide. */
  getSession(): Session | null {
    const session = this.readRawSession();
    if (!session) return null;
    if (new Date(session.expire_le) <= new Date()) {
      AuthService.markSessionExpiredNotice();
      this.logout();
      return null;
    }
    // Double écriture : sessionStorage (A-34) + localStorage (reprise après redémarrage Electron)
    if (!sessionStorage.getItem(AuthService.STORAGE_KEY)) {
      this.persistSession(session);
    }
    return session;
  }

  /** Détruit la session courante. */
  logout(): void {
    localStorage.removeItem(AuthService.STORAGE_KEY);
    sessionStorage.removeItem(AuthService.STORAGE_KEY);
  }

  /** Lève si l'utilisateur n'a pas le rôle requis (session en mémoire uniquement). */
  requireRole(...allowed: Role[]): Session {
    const s = this.getSession();
    if (!s) throw new AuthError('SESSION_EXPIRED');
    if (!allowed.includes(s.role)) throw new AuthError('INSUFFICIENT_ROLE');
    return s;
  }

  /** Recharge le rôle depuis la base — bloque la falsification sessionStorage. */
  async requireRoleFresh(...allowed: Role[]): Promise<Session> {
    const session = await this.refreshSessionFromDb();
    if (!session) throw new AuthError('SESSION_EXPIRED');
    if (!allowed.includes(session.role)) throw new AuthError('INSUFFICIENT_ROLE');
    return session;
  }

  /** Synchronise la session avec l'état utilisateur en base (rôle, actif). */
  async refreshSessionFromDb(): Promise<Session | null> {
    if (this.remoteRefresh) return this.remoteRefresh();
    const s = this.getSession();
    if (!s) return null;
    let user: Awaited<ReturnType<IUserRepository['findById']>>;
    try {
      user = await this.userRepo.findById(s.user_id);
    } catch {
      // Erreur transitoire de persistance : ne pas éjecter l’utilisateur
      return s;
    }
    if (!user || !user.actif) {
      this.logout();
      return null;
    }

    const access = user.accessible_tenant_ids;
    const sessionTenantAllowed =
      user.tenant_id === s.tenant_id ||
      access === 'all' ||
      (Array.isArray(access) && access.includes(s.tenant_id));

    const nextTenant = sessionTenantAllowed ? s.tenant_id : user.tenant_id;
    const freshGrants = sessionGrantsFromUser(user);
    if (
      user.role !== s.role ||
      nextTenant !== s.tenant_id ||
      JSON.stringify(s.grants) !== JSON.stringify(freshGrants)
    ) {
      const updated: Session = {
        ...s,
        role: user.role,
        tenant_id: nextTenant,
        grants: freshGrants,
      };
      this.persistSession(updated);
      return updated;
    }
    return s;
  }

  /** Remplace les droits Cabinet portés par la session (après un octroi d'accès à une entreprise). */
  adoptSessionGrants(grants: SessionGrants): void {
    const s = this.getSession();
    if (!s) return;
    this.persistSession({ ...s, grants });
  }

  /**
   * Switch Multi-entités : met à jour le tenant de session sans changer l'utilisateur.
   * MD-02 : applique le rôle du dossier (`roles_by_tenant`) si présent.
   * Le contrôle d'accès portefeuille est fait en amont (CabinetPortfolioService).
   */
  async adoptSessionTenant(tenantId: string): Promise<Session | null> {
    const s = this.getSession();
    if (!s) return null;
    let role = s.role;
    try {
      const user = await this.userRepo.findById(s.user_id);
      const mapped = user
        ? roleForTenant(user, tenantId)
        : s.grants
          ? roleForTenantFromGrants(s.grants, tenantId)
          : null;
      if (mapped) role = mapped;
    } catch {
      const mapped = s.grants ? roleForTenantFromGrants(s.grants, tenantId) : null;
      if (mapped) role = mapped;
      /* repli : conserver le rôle de session */
    }
    const updated: Session = { ...s, tenant_id: tenantId, role };
    this.persistSession(updated);
    return updated;
  }
}
